import React, { useState, useEffect, useRef } from 'react';
import { sourcesVer2, sourcesTos, timeframes, timeframesTos } from '../../utils/helpers';
import theme from '../../theme';

const STSTimelineList = ({
  handleSetSTS,
  context,
  initialData = {
    updown_target: null, fade_target: null, trading_spagat: false,
    jump_to: false, shabbat: false, fade_shabbat: false,
    upper_bound: null, lower_bound: null, j2s_target: null, j2f_target: null
  }
}) => {
  const [lowerBound, setLowerBound] = useState('');
  const [upperBound, setUpperBound] = useState('');
  const [fadeTarget, setFadeTarget] = useState('');
  const [updownTarget, setUpdownTarget] = useState('');
  const [jumpSignalTarget, setJumpSignalTarget] = useState('');
  const [jumpTimeframeTarget, setJumpTimeframeTarget] = useState('');
  const [fadeShabbat, setFadeShabbat] = useState(false);
  const [shabbat, setShabbat] = useState(false);

  const [isOpen, setIsOpen] = useState(initialData.updown_target || initialData.fade_target || initialData.fade_shabbat || initialData.trading_spagat || initialData.jump_to || initialData.shabbat);
  const contentRef = useRef(null);

  const dropdowns = { "Lower Bound": false, "Upper Bound": false, "Signal": false, "Timeframe": false, "Updown": false, "Fade": false };
  const isDropdownOpen = () => { return Number(dropdowns["Lower Bound"]) + Number(dropdowns["Upper Bound"]) + Number(dropdowns["Signal"]) + Number(dropdowns["Timeframe"]) + Number(dropdowns["Fade"]) + Number(dropdowns["Updown"]); }

  useEffect(() => {
    setLowerBound(initialData.lower_bound);
    setUpperBound(initialData.upper_bound);
    setFadeTarget(initialData.fade_target);
    setUpdownTarget(initialData.updown_target);
    setJumpSignalTarget(initialData.j2s_target);
    setJumpTimeframeTarget(initialData.j2f_target);
    setShabbat(initialData.shabbat);
    setFadeShabbat(initialData.fade_shabbat);
  }, [initialData]);

  const handleSubmit = () => {
    const data = {
      updown_target: updownTarget || null, fade_target: fadeTarget || null,
      lower_bound: lowerBound || null, upper_bound: upperBound || null,
      j2s_target: jumpSignalTarget || null, j2f_target: jumpTimeframeTarget || null,
      fade_shabbat: fadeShabbat, shabbat: shabbat
    };
    handleSetSTS(data);
  };

  const getCircleState = (itemIndex) => {
    switch (itemIndex) {
      case 0: return (initialData.fade_target || initialData.updown_target) ? 'active-green' : (fadeTarget || updownTarget) ? 'filled' : 'hollow';
      case 1: return (initialData.trading_spagat && !initialData.updown_target && !initialData.fade_target) ? 'active-green' : (lowerBound || upperBound) ? 'filled' : 'hollow';
      case 2: return (initialData.jump_to && !initialData.trading_spagat && !initialData.updown_target && !initialData.fade_target) ? 'active-green' : (jumpSignalTarget || jumpTimeframeTarget) ? 'filled' : 'hollow';
      case 3: return (initialData.shabbat && !initialData.trading_spagat && !initialData.jump_to && !initialData.updown_target && !initialData.fade_target) ? 'active-green' : shabbat ? 'filled' : 'hollow';
      default: return 'hollow';
    }
  };

  const CircleIndicator = ({ state }) => {
    const baseClasses = "w-4 h-4 rounded-full border-2 flex-shrink-0";
    switch (state) {
      case 'active-green':
        return (
          <span className="relative flex size-4" style={{ zIndex: 10000 }}>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75"></span>
            <span className="relative inline-flex size-4 rounded-full bg-emerald-500"></span>
          </span>
        );
      case 'filled':
        return <div className={`${baseClasses} bg-olive-500 border-olive-500`} style={{ zIndex: 10000 }} />;
      case 'hollow':
      default:
        return <div className={`${baseClasses} bg-white border-olive-300`} style={{ zIndex: 10000 }} />;
    }
  };

  const isDropdownAllowed = (label, state) => {
    if (state === false) { dropdowns[label] = state; }
    if (isDropdownOpen() === 0) { dropdowns[label] = state; return true && state; }
    return false;
  }

  const CustomDropdown = ({ active, value, onChange, options, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <div className="relative overflow-visible">
        {label && <label className={`block text-xs font-semibold ${theme.textSecondary} mb-1 tracking-wide uppercase truncate`}>{label}</label>}
        <div className="relative">
          <button
            type="button"
            onClick={() => active ? setIsOpen(isDropdownAllowed(label, !isOpen)) : {}}
            className={`w-full px-3 py-2 text-left ${theme.input} ${active ? 'cursor-pointer' : 'cursor-default'} flex items-center justify-between`}
          >
            <span className={`${value ? theme.textPrimary : theme.textMuted} overflow-hidden`}>{value || placeholder}</span>
            <svg className={`w-4 h-4 ${theme.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isOpen && (
            <div className="absolute z-10 w-50 mt-1 bg-white border border-olive-200 rounded-xl shadow-lg max-h-60 overflow-auto">
              <div className={`px-3 py-2 hover:bg-olive-50 rounded-t-xl cursor-pointer ${theme.textMuted} text-sm`} onClick={() => { onChange(''); setIsOpen(false); }}>{placeholder}</div>
              {options.map((option) => (
                <div key={option} className={`px-3 py-2 hover:bg-olive-50 cursor-pointer ${theme.textPrimary} text-sm last:rounded-b-xl`} onClick={() => { onChange(option); setIsOpen(false); }}>{option}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`max-w-md my-4 mx-auto ${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5 overflow-visible`}>
      <button
        className={`p-3 ${isOpen ? "mb-4" : "mb-2"} w-full rounded-xl text-center text-sm font-semibold transition-all duration-200 ${isOpen ? theme.btnPrimary : theme.btnSecondary
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'Hide' : 'Short Term Strategy'}
      </button>
      <div
        ref={contentRef}
        style={{ maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px' }}
        className={`overflow-${isOpen ? "visible" : "hidden"} transition-max-height duration-500 ease-in-out`}
      >
        <div className='space-y-6 p-4 transition-opacity duration-500 ease-in-out' style={{ opacity: isOpen ? 100 : 0 }}>
          <div className="relative">
            <div className="absolute top-2 bottom-0 w-0.5 bg-olive-200" style={{ zIndex: 1, marginLeft: "7px" }}></div>

            {context !== 'tos' && (
              <div className="flex items-start space-x-4 mb-8">
                <CircleIndicator state={getCircleState(0)} />
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-bold ${theme.textPrimary} mb-2`}>Updown</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CustomDropdown active={isOpen} value={updownTarget} onChange={setUpdownTarget} options={sourcesVer2} placeholder="Select..." label="Updown Target" />
                    <CustomDropdown active={isOpen} value={fadeTarget} onChange={setFadeTarget} options={sourcesVer2} placeholder="Select..." label="Fade Target" />
                    <button
                      onClick={() => isOpen ? setFadeShabbat(!fadeShabbat) : {}}
                      className={`text-sm font-semibold transition-all p-2 rounded-xl ${fadeShabbat ? 'text-olive-800 bg-olive-100' : `${theme.textPrimary} hover:bg-olive-50`} ${isOpen ? 'cursor-pointer' : 'cursor-default'}`}
                    >Shabbat</button>
                  </div>
                </div>
              </div>
            )}

            {context !== 'tos' && (
              <div className="flex items-start space-x-4 mb-8">
                <CircleIndicator state={getCircleState(1)} />
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-bold ${theme.textPrimary} mb-2`}>Trading Spagat</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CustomDropdown active={isOpen} value={lowerBound} onChange={setLowerBound} options={sourcesVer2} placeholder="Select..." label="Lower Bound" />
                    <CustomDropdown active={isOpen} value={upperBound} onChange={setUpperBound} options={sourcesVer2} placeholder="Select..." label="Upper Bound" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-4 mb-8">
              <CircleIndicator state={getCircleState(2)} />
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm font-bold ${theme.textPrimary} mb-2`}>Jump to</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <CustomDropdown active={isOpen} value={jumpSignalTarget} onChange={setJumpSignalTarget} options={context !== 'tos' ? sourcesVer2 : sourcesTos} placeholder="Select..." label="Signal" />
                  <CustomDropdown active={isOpen} value={jumpTimeframeTarget} onChange={setJumpTimeframeTarget} options={context !== 'tos' ? timeframes : timeframesTos} placeholder="Select..." label="Timeframe" />
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <CircleIndicator state={getCircleState(3)} />
              <div className="flex-1">
                <button
                  onClick={() => isOpen ? setShabbat(!shabbat) : {}}
                  className={`text-sm font-semibold transition-all p-2 rounded-xl ${shabbat ? 'text-olive-800 bg-olive-100' : `${theme.textPrimary} hover:bg-olive-50`} ${isOpen ? 'cursor-pointer' : 'cursor-default'}`}
                >Shabbat</button>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 px-4 pb-2">
          <button onClick={() => isOpen ? handleSubmit() : {}} className={`w-full px-4 py-2.5 rounded-xl text-sm font-semibold ${theme.btnPrimary}`}>
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default STSTimelineList;